// src/utils/guiltAlgorithm.js

/**
 * Enhanced guilt algorithm that takes multiple factors into account
 * for a more accurate and personalized guilt score calculation
 */
export const calculateEnhancedGuiltScore = (data, userProfile = {}) => {
  let score = 50; // Start neutral
  
  // Base adjustments from current logic
  if (data.mood === "sad" || data.mood === "bored") score += 15;
  if (data.mood === "fomo") score += 20;
  if (data.mood === "drunk") score += 25;
  if (data.mood === "happy") score -= 5;
  if (data.mood === "celebrating") score -= 10;
  
  // Factor 1: Price analysis
  const amount = parseFloat(data.amount);
  
  // If user has provided income bracket, adjust amount impact based on income
  if (userProfile.incomeBracket) {
    const incomeFactors = {
      'under-25k': 1.5,
      '25k-50k': 1.2,
      '50k-75k': 1.0,
      '75k-100k': 0.8,
      'over-100k': 0.6
    };
    
    const factor = incomeFactors[userProfile.incomeBracket] || 1.0;
    
    if (amount > 500) score += 25 * factor;
    else if (amount > 200) score += 15 * factor;
    else if (amount > 100) score += 10 * factor;
    else if (amount > 50) score += 5 * factor;
    else if (amount < 20) score -= 10 * factor;
  } else {
    // Use original logic if no income data
    if (amount > 500) score += 25;
    else if (amount > 200) score += 15;
    else if (amount > 100) score += 10;
    else if (amount > 50) score += 5;
    else if (amount < 20) score -= 10;
  }
  
  // Factor 2: Purchase necessity
  if (data.necessity === "essential") score -= 20;
  if (data.necessity === "useful") score -= 10;
  if (data.necessity === "luxury") score += 15;
  
  // Factor 3: Usage frequency
  if (data.usageFrequency === "daily") score -= 15;
  if (data.usageFrequency === "weekly") score -= 10;
  if (data.usageFrequency === "occasionally") score += 5;
  if (data.usageFrequency === "rarely") score += 15;
  if (data.usageFrequency === "never") score += 25;
  
  // Factor 4: Still happy about it
  if (data.stillHappy === "yes") score -= 15;
  if (data.stillHappy === "kinda") score += 0;
  if (data.stillHappy === "no") score += 20;
  
  // Factor 5: Would buy again
  if (data.buyAgain === "hellyes") score -= 15;
  if (data.buyAgain === "maybe") score += 5;
  if (data.buyAgain === "nope") score += 20;
  
  // Factor 6: Budget impact
  if (userProfile.monthlyBudget && amount) {
    const budgetImpact = (amount / userProfile.monthlyBudget) * 100;
    if (budgetImpact > 20) score += 25;
    else if (budgetImpact > 10) score += 15;
    else if (budgetImpact > 5) score += 5;
  }
  
  // Factor 7: Recent similar purchases (purchase frequency)
  if (userProfile.recentPurchases && data.category) {
    const similarPurchases = userProfile.recentPurchases.filter(
      p => p.category === data.category && 
      new Date(p.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    );
    
    if (similarPurchases.length >= 3) score += 20;
    else if (similarPurchases.length === 2) score += 10;
    else if (similarPurchases.length === 1) score += 5;
  }
  
  // Ensure score is between 0 and 100
  return Math.max(0, Math.min(100, Math.round(score)));
};

/**
 * Generate personalized advice based on purchase and guilt score
 */
export const generateAdvice = (purchase, score) => {
  if (score < 30) {
    return {
      emoji: "👍",
      title: "Low Guilt - Good Purchase!",
      message: "This seems like a solid purchase that aligns with your needs and values.",
      tips: [
        "Consider setting aside the same amount for savings next time",
        "Track how often you use this item to validate it was worth it"
      ]
    };
  } else if (score < 70) {
    return {
      emoji: "🤔",
      title: "Medium Guilt - Mixed Feelings",
      message: `Your ${purchase.item} falls in the "maybe" zone - not terrible, but not guilt-free either.`,
      tips: [
        "Wait 24 hours before similar purchases in the future",
        "Consider if there were cheaper alternatives",
        "Track your usage to justify the expense"
      ]
    };
  } else {
    return {
      emoji: "😬",
      title: "High Guilt - Possible Regret",
      message: "This purchase is giving you significant guilt feelings.",
      tips: [
        "Try the 30-day rule for future impulse buys",
        "Make a wish list and revisit it after a cooling period",
        "Consider creating a splurge budget for guilt-free treats"
      ]
    };
  }
};

/**
 * Spending pattern analysis from purchase history
 */
export const analyzeSpendingPatterns = (purchases) => {
  if (!purchases || purchases.length < 5) return null;
  
  // Calculate total spent
  const totalSpent = purchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  
  // Calculate average guilt
  const avgGuilt = purchases.reduce((sum, p) => sum + p.guiltScore, 0) / purchases.length;
  
  // Find most common mood
  const moodCounts = purchases.reduce((acc, p) => {
    acc[p.mood] = (acc[p.mood] || 0) + 1;
    return acc;
  }, {});
  const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0][0];
  
  // Find most common guilt level
  const guiltLevels = purchases.reduce((acc, p) => {
    const level = p.guiltScore < 30 ? 'low' : p.guiltScore < 70 ? 'medium' : 'high';
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {});
  const topGuiltLevel = Object.entries(guiltLevels).sort((a, b) => b[1] - a[1])[0][0];
  
  // Calculate percentage of impulse buys
  const impulseBuys = purchases.filter(p => 
    p.mood === "bored" || p.mood === "sad" || p.mood === "drunk" || p.mood === "fomo"
  ).length;
  const impulsePercentage = (impulseBuys / purchases.length) * 100;
  
  // Analyze financial impact
  const monthlyAverage = totalSpent / (purchases.length > 30 ? purchases.length / 30 : 1);
  
  // Calculate trend (is average guilt increasing or decreasing?)
  const recentPurchases = purchases.slice(0, Math.min(5, purchases.length));
  const recentAvgGuilt = recentPurchases.reduce((sum, p) => sum + p.guiltScore, 0) / recentPurchases.length;
  const trend = recentAvgGuilt < avgGuilt ? "improving" : "worsening";
  
  return {
    totalSpent,
    avgGuilt,
    topMood,
    topGuiltLevel,
    impulsePercentage,
    monthlyAverage,
    trend,
    purchaseCount: purchases.length
  };
};

/**
 * Generate detailed shopping persona based on purchase history
 */
export const generateDetailedPersona = (purchases) => {
  if (!purchases || purchases.length < 5) return null;
  
  const patterns = analyzeSpendingPatterns(purchases);
  
  // Determine base persona
  let personaType;
  
  if (patterns.topMood === "impulsive" && patterns.avgGuilt > 70) {
    personaType = "Emotional Spender";
  } else if (patterns.topMood === "celebrating" && patterns.avgGuilt < 40) {
    personaType = "Reward Shopper";
  } else if (patterns.avgGuilt > 60) {
    personaType = "Guilty Indulger";
  } else if (patterns.avgGuilt < 30) {
    personaType = "Practical Purchaser";
  } else if (patterns.impulsePercentage > 60) {
    personaType = "Browse & Buy Shopper";
  } else {
    personaType = "Balanced Buyer";
  }
  
  // Create detailed persona object with tips
  const personas = {
    "Emotional Spender": {
      description: "You often shop to boost your mood or when feeling down.",
      traits: ["Mood-driven purchases", "Higher guilt afterwards", "Spontaneous decisions"],
      tips: [
        "Try the 24-hour rule before completing purchases",
        "Find non-shopping activities that boost your mood",
        "Make a wish list instead of buying immediately"
      ]
    },
    "Reward Shopper": {
      description: "You use shopping as a way to celebrate or reward yourself.",
      traits: ["Treats purchases as achievements", "Generally feels good about buys", "Associates shopping with positive feelings"],
      tips: [
        "Set specific milestones for rewards to avoid over-treating",
        "Consider non-material rewards sometimes",
        "Create a 'celebration budget' for guilt-free splurges"
      ]
    },
    "Guilty Indulger": {
      description: "You enjoy luxury purchases but often feel guilty afterwards.",
      traits: ["Attracted to premium items", "Post-purchase anxiety", "Conflicted shopping values"],
      tips: [
        "Budget specifically for 'luxury' items to reduce guilt",
        "Focus on quality over quantity",
        "Consider the cost-per-use for expensive items"
      ]
    },
    "Practical Purchaser": {
      description: "You prioritize value and necessity in your shopping decisions.",
      traits: ["Research-driven", "Low shopping guilt", "Value-oriented decisions"],
      tips: [
        "Don't forget to occasionally treat yourself",
        "Your approach is healthy - keep it up!",
        "Consider setting aside 'fun money' for spontaneous purchases"
      ]
    },
    "Browse & Buy Shopper": {
      description: "You're drawn to shopping without specific plans, leading to unplanned purchases.",
      traits: ["Shopping as entertainment", "Discovers 'needs' while browsing", "Varied guilt levels"],
      tips: [
        "Make shopping lists before browsing",
        "Set spending limits for browsing sessions",
        "Try 'window shopping' without your wallet occasionally"
      ]
    },
    "Balanced Buyer": {
      description: "You have a healthy mix of practical and fun purchases.",
      traits: ["Moderate guilt levels", "Mix of planned and spontaneous buys", "Generally balanced approach"],
      tips: [
        "You have a healthy approach - maintain your balance",
        "Continue being mindful of shopping triggers",
        "Consider tracking to make sure you stay balanced"
      ]
    }
  };
  
  return {
    type: personaType,
    ...personas[personaType],
    stats: patterns
  };
};
